// The Kennisbank admin (views/Admin/AdminKennisbank.vue): admins curate the
// content of ai.valsplat.team (the kennisbank/ app), which reads the same
// Firestore docs. Field names and enum values here are a contract with that
// app. Pure, so it is unit-tested in tests/unit/kennisbank.test.js.

const text = (v) => (typeof v === "string" ? v.trim() : "");

// kb_skill_meta/{skillId}.phase; null means no phase.
export const KB_PHASES = [
  { value: "acquisitie", label: "Acquisitie" },
  { value: "opzet", label: "Opzet" },
  { value: "analyse", label: "Analyse" },
  { value: "rapportage", label: "Rapportage" },
  { value: "vervolg", label: "Vervolg" },
];

// kb_skill_meta/{skillId}.summary: a soft limit, shown as a counter.
export const KB_SUMMARY_MAX = 200;

// kb_tools/{id}.icon
export const KB_TOOL_ICONS = [
  "research",
  "lightbulb",
  "chat",
  "automatic",
  "design",
  "document",
  "strategy",
  "star",
];

// kb_pages/{pageId}: the pages the site renders.
export const KB_PAGES = [{ id: "skills-uitleg", label: "Skills uitleg" }];

// kb_videos/{id}.status, in display order.
export const KB_VIDEO_GROUPS = [
  { status: "pending", label: "Pending" },
  { status: "approved", label: "Approved" },
  { status: "rejected", label: "Rejected" },
];

// kb_wishes/{id}.status
export const KB_WISH_STATUSES = [
  { value: "open", label: "Open" },
  { value: "planned", label: "Planned" },
  { value: "done", label: "Done" },
  { value: "closed", label: "Closed" },
];

// A ms number from a ms number, a Firestore Timestamp or {seconds}; null
// otherwise. The site may write either.
export function toMillis(v) {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v && typeof v.toMillis === "function") return v.toMillis();
  if (v && typeof v.seconds === "number") return v.seconds * 1000;
  return null;
}

// Only http(s) links become an href: video urls are typed by colleagues.
export function safeUrl(url) {
  const u = text(url);
  return /^https?:\/\//i.test(u) ? u : "";
}

export function normalisePhase(value) {
  return KB_PHASES.some((p) => p.value === value) ? value : null;
}

// The first sentence of a (markdown) skill description, used as the summary
// placeholder. A sentence ends at . ! or ? followed by a capital, a digit or
// the end, so "bijv. iets" doesn't cut it short.
export function firstSentence(description, max = KB_SUMMARY_MAX) {
  if (typeof description !== "string") return "";
  const plain = description
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}(?:#{1,6}|[-*+]|\d+\.|>)\s+/gm, "")
    .replace(/[*_`~]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
  if (!plain) return "";
  const m = plain.match(/^.+?[.!?](?=\s+[\p{Lu}\d"'“‘(]|$)/u);
  const sentence = m ? m[0] : plain;
  return sentence.length > max
    ? `${sentence.slice(0, max - 1).trimEnd()}…`
    : sentence;
}

// One editable row per published skill, merged with its kb_skill_meta doc.
export function skillRows(skills, metaById = {}) {
  return (skills || [])
    .filter((s) => s && s.status === "published")
    .map((s) => {
      const pub = s.published || {};
      const meta = metaById[s.id] || {};
      return {
        id: s.id,
        title: text(pub.title) || text(s.title) || s.id,
        category: text(pub.category) || text(s.category),
        placeholder: firstSentence(pub.description),
        phase: normalisePhase(meta.phase),
        summary: typeof meta.summary === "string" ? meta.summary : "",
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title, "nl"));
}

// What a row saves to kb_skill_meta/{id}.
export function skillMetaPayload(row, uid, now = Date.now()) {
  return {
    phase: normalisePhase(row.phase),
    summary: text(row.summary),
    updatedAt: now,
    updatedBy: uid || null,
  };
}

// Whether a row differs from what was last loaded or saved ({phase, summary}).
export function isSkillMetaDirty(row, saved) {
  const base = saved || { phase: null, summary: "" };
  return (
    normalisePhase(row.phase) !== normalisePhase(base.phase) ||
    text(row.summary) !== text(base.summary)
  );
}

// Tools in site order: `order` ascending, unordered last, then by name.
export function sortTools(tools) {
  const ord = (t) =>
    typeof t.order === "number" && Number.isFinite(t.order)
      ? t.order
      : Infinity;
  return [...(tools || [])].sort(
    (a, b) => ord(a) - ord(b) || text(a.name).localeCompare(text(b.name), "nl")
  );
}

// The order for a new tool: after the last one.
export function nextToolOrder(tools) {
  const orders = (tools || [])
    .map((t) => t.order)
    .filter((o) => typeof o === "number" && Number.isFinite(o));
  return orders.length ? Math.max(...orders) + 1 : 1;
}

// What the tool editor saves to kb_tools/{id}.
export function toolPayload(draft, now = Date.now()) {
  const order = Number(draft.order);
  return {
    name: text(draft.name),
    summary: text(draft.summary),
    body: text(draft.body),
    url: text(draft.url),
    linkLabel: text(draft.linkLabel),
    icon: KB_TOOL_ICONS.includes(draft.icon) ? draft.icon : "star",
    order: Number.isFinite(order) ? order : 0,
    updatedAt: now,
  };
}

// The tool editor's error, or "" when it can be saved.
export function toolError(draft) {
  if (!text(draft.name)) return "A tool needs a name.";
  if (text(draft.url) && !safeUrl(draft.url)) {
    return "The URL must start with https:// (or http://).";
  }
  if (!Number.isFinite(Number(draft.order))) return "Order must be a number.";
  return "";
}

// What a page editor saves to kb_pages/{pageId}.
export function pagePayload(draft, now = Date.now()) {
  return {
    title: text(draft.title),
    intro: text(draft.intro),
    body: text(draft.body),
    updatedAt: now,
  };
}

// Videos in the three status groups, newest first. An unknown status counts
// as pending, so it still gets reviewed.
export function groupVideos(videos) {
  const known = KB_VIDEO_GROUPS.map((g) => g.status);
  const newestFirst = (a, b) =>
    (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0);
  return KB_VIDEO_GROUPS.map((g) => ({
    ...g,
    items: (videos || [])
      .filter((v) =>
        g.status === "pending"
          ? !known.includes(v.status) || v.status === "pending"
          : v.status === g.status
      )
      .sort(newestFirst),
  }));
}

// Vote counts per wish id, from the parent wish ids of every kb_votes doc
// (one collectionGroup read: d.ref.parent.parent.id).
export function countVotes(wishIds) {
  const counts = {};
  for (const id of wishIds || []) {
    if (id) counts[id] = (counts[id] || 0) + 1;
  }
  return counts;
}

// Wishes with their vote count, most votes first, then newest first.
export function rankWishes(wishes, counts = {}) {
  return (wishes || [])
    .map((w) => ({ ...w, votes: counts[w.id] || 0 }))
    .sort(
      (a, b) =>
        b.votes - a.votes ||
        (toMillis(b.createdAt) || 0) - (toMillis(a.createdAt) || 0)
    );
}
